// WHAT: React component for displaying a single TimeTracQ workspace and its aspects
// WHY:  Provides a dedicated, reusable UI card for managing a workspace's aspects, including expansion/collapse
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: UI components, AspectCard component, AspectConfig type
// RISKS:
//   - WARN: Aspect changes are immediate in local state but not persisted until explicit save action in parent.
//   - NOTE: Relies on parent for expansion state and all aspect update callbacks.

import type { AspectConfig } from "../types";

import { AspectCard } from "./AspectCard";

/**
 * WHAT:
 *   Props interface for the `WorkspaceCard` component.
 *
 * WHY:
 *   Defines the expected inputs for `WorkspaceCard`, ensuring type safety and clarity
 *   regarding the data and callback functions it receives.
 */
interface WorkspaceCardProperties {
  aspects: Record<string, AspectConfig>;
  isExpanded: boolean;
  onToggleBoolean: (aspectName: string, field: "carryover" | "count_hours" | "count_plan") => void;
  onToggleExpansion: () => void;
  onUpdateColor: (aspectName: string, color: string) => void;
  onUpdateHours: (aspectName: string, hours: string) => void;
  onUpdateStatus: (aspectName: string, status: string) => void;
  workspaceName: string;
}

/**
 * WHAT:
 *   React component that renders a card for a single TimeTracQ workspace.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for an individual workspace, making the
 *   main component cleaner and improving reusability.
 *   It displays the workspace name, the number of aspects, and a list of `AspectCard` components.
 *   It also handles the expansion and collapse of the aspect list.
 *
 * PROPS:
 *   - workspaceName: string — The name of the workspace.
 *   - aspects: Record<string, AspectConfig> — A map of aspect names to their configurations.
 *   - isExpanded: boolean — Indicates whether the aspect list for this workspace is expanded.
 *   - onToggleExpansion: () => void — Callback to toggle the expansion state of the workspace.
 *   - onUpdateHours: (aspectName: string, hours: string) => void — Callback for updating an aspect's hours.
 *   - onUpdateStatus: (aspectName: string, status: string) => void — Callback for updating an aspect's status.
 *   - onUpdateColor: (aspectName: string, color: string) => void — Callback for updating an aspect's color.
 *   - onToggleBoolean: (aspectName: string, field: 'carryover' | 'count_hours' | 'count_plan') => void — Callback for toggling an aspect's boolean flags.
 *
 * BEHAVIOR:
 *   - Displays a header with the workspace name and aspect count.
 *   - Renders `AspectCard` components for each aspect, showing only the first 4 if not expanded.
 *   - Provides a "Show More/Less" button to toggle the visibility of additional aspects.
 *   - Passes down aspect update callbacks to `AspectCard` components.
 *
 * RISKS:
 *   - WARN: Changes made within nested `AspectCard` components are reflected in the parent's state
 *           immediately but are not persisted to the database until the top-level `saveConfig` is called.
 *   - NOTE: The component relies heavily on prop drilling for aspect updates, which could become
 *           cumbersome for deeply nested structures without a global state management solution.
 *
 * @param root0
 *
 * @param root0.workspaceName
 *
 * @param root0.aspects
 *
 * @param root0.isExpanded
 *
 * @param root0.onToggleExpansion
 *
 * @param root0.onUpdateHours
 *
 * @param root0.onUpdateStatus
 *
 * @param root0.onUpdateColor
 *
 * @param root0.onToggleBoolean
 *
 * @returns {JSX.Element} Workspace card component
 *
 */
export function WorkspaceCard({
  aspects,
  isExpanded,
  onToggleBoolean,
  onToggleExpansion,
  onUpdateColor,
  onUpdateHours,
  onUpdateStatus,
  workspaceName,
}: Readonly<WorkspaceCardProperties>) {
  const aspectEntries = Object.entries(aspects);
  const displayedAspects = isExpanded ? aspectEntries : aspectEntries.slice(0, 4);

  return (
    <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden hover:border-slate-600/50 transition-all duration-300">
      {/* Workspace Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{workspaceName}</h3>
          <div className="text-sm text-slate-400 bg-slate-600/50 px-2 py-1 rounded-full">
            {aspectEntries.length} Aspects
          </div>
        </div>
      </div>

      {/* Aspects List */}
      <div className="p-6 space-y-4">
        {displayedAspects.map(([aspectName, aspect]) => (
          <AspectCard
            aspect={aspect}
            aspectName={aspectName}
            key={aspectName}
            onToggleBoolean={onToggleBoolean}
            onUpdateColor={onUpdateColor}
            onUpdateHours={onUpdateHours}
            onUpdateStatus={onUpdateStatus}
            workspaceName={workspaceName}
          />
        ))}

        {/* Show More/Less Button */}
        {aspectEntries.length > 4 && (
          <div className="pt-2">
            <button
              className="w-full py-3 px-4 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 hover:border-slate-500/50 rounded-lg transition-all duration-200 group"
              onClick={onToggleExpansion}
            >
              <div className="flex items-center justify-center space-x-2">
                <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
                  {isExpanded ? "Collapse" : `${String(aspectEntries.length - 4)} more aspects`}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-400 group-hover:text-white transition-all duration-200 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19 9l-7 7-7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
